import { useMemo, useState } from "react";
import Icon from "./Icon";
import LoadingBlock from "./LoadingBlock";
import EmptyState from "./EmptyState";
import { useApi } from "../hooks/useApi";
import { listTodos, createTodo, updateTodo, deleteTodo } from "../api/todoService";
import { formatDate, formatDateTime, daysFromToday } from "../utils/formatters";
import { showApiError, showToast } from "../utils/toast";

// Self-contained todo list. Fetches its own data so pages stay thin.
//   params      — query for listTodos (e.g. {} for self, { employee_id } for admin)
//   createExtra — fields merged into every createTodo (e.g. { employee_id } to assign)
//   canManage   — show edit/delete/toggle affordances (default true)
//   canAdd      — show the quick-add row (default true; off for the "all" view)
//   showOwner   — tag each row with its owner's name (for the aggregate view)
//   emptyHint   — empty-state hint text
export default function TodoList({ params = {}, createExtra = {}, canManage = true, canAdd = true, showOwner = false, emptyHint }) {
  const key = JSON.stringify(params);
  const todosApi = useApi(() => listTodos(params), [key]);
  const rows = useMemo(
    () => (Array.isArray(todosApi.data?.data) ? todosApi.data.data : []),
    [todosApi.data],
  );

  const [filter, setFilter] = useState("active"); // all | active | done
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [remind, setRemind] = useState(""); // datetime-local string
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((t) => !t.is_done).length,
    done: rows.filter((t) => t.is_done).length,
  }), [rows]);

  const visible = rows.filter((t) =>
    filter === "all" ? true : filter === "done" ? t.is_done : !t.is_done,
  );

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setAdding(true);
    try {
      await createTodo({
        title: t,
        due_date: due || null,
        // datetime-local omits the timezone — backend parses with Carbon
        // (server tz) which is fine for the firm's single-timezone use.
        remind_at: remind || null,
        ...createExtra,
      });
      setTitle("");
      setDue("");
      setRemind("");
      showToast("success", createExtra.employee_id ? "Todo assigned" : "Todo added");
      todosApi.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (todo) => {
    try {
      await updateTodo(todo.id, { is_done: !todo.is_done });
      todosApi.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const saveEdit = async (todo, patch) => {
    try {
      await updateTodo(todo.id, patch);
      setEditingId(null);
      showToast("success", "Todo updated");
      todosApi.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const remove = async (todo) => {
    if (!window.confirm(`Delete "${todo.title}"?`)) return;
    try {
      await deleteTodo(todo.id);
      showToast("success", "Todo deleted");
      todosApi.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="col gap-md">
      {canManage && canAdd && (
        <div className="card" style={{ padding: 12 }}>
          <div className="row gap-sm" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 2, minWidth: 200, marginBottom: 0 }}>
              <label className="field-label">Title</label>
              <input
                className="input"
                placeholder="Add a todo…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              />
            </div>
            <div className="field" style={{ minWidth: 150, marginBottom: 0 }}>
              <label className="field-label">Due date <span className="t-muted text-xs">(optional)</span></label>
              <input
                type="date"
                className="input"
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </div>
            <div className="field" style={{ minWidth: 200, marginBottom: 0 }}>
              <label className="field-label">Remind me at <span className="t-muted text-xs">(optional)</span></label>
              <input
                type="datetime-local"
                className="input"
                value={remind}
                onChange={(e) => setRemind(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={add} disabled={adding || !title.trim()}>
              <Icon name="plus" size={14} /> {adding ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        {[
          { id: "active", label: "Active", count: counts.active },
          { id: "done", label: "Done", count: counts.done },
          { id: "all", label: "All", count: counts.all },
        ].map((f) => (
          <button key={f.id} className={`chip ${filter === f.id ? "active" : ""}`} onClick={() => setFilter(f.id)}>
            {f.label} <span className="chip-count">{f.count}</span>
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {todosApi.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="check-square"
              title={filter === "done" ? "Nothing completed yet" : "No todos"}
              hint={emptyHint || (canManage ? "Add one above to get started." : undefined)}
            />
          ) : (
            <div className="col">
              {visible.map((t) => (
                <TodoRow
                  key={t.id}
                  todo={t}
                  canManage={canManage}
                  showOwner={showOwner}
                  editing={editingId === t.id}
                  onToggle={() => toggle(t)}
                  onStartEdit={() => setEditingId(t.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onSave={(patch) => saveEdit(t, patch)}
                  onDelete={() => remove(t)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TodoRow({ todo, canManage, showOwner = false, editing, onToggle, onStartEdit, onCancelEdit, onSave, onDelete }) {
  // "Assigned by" — creator differs from the owner (typically an admin assigned it).
  const assignedBy = todo.creator && Number(todo.creator.id) !== Number(todo.user_id)
    ? todo.creator.name
    : null;
  const overdue = todo.due_date && !todo.is_done && daysFromToday(todo.due_date) < 0;

  if (editing) {
    return <TodoEditForm todo={todo} onCancel={onCancelEdit} onSave={onSave} />;
  }

  return (
    <div
      className="row"
      style={{
        gap: 12,
        padding: "10px 14px",
        borderBottom: "1px solid var(--border)",
        alignItems: "flex-start",
      }}
    >
      <input
        type="checkbox"
        checked={!!todo.is_done}
        disabled={!canManage}
        onChange={onToggle}
        style={{ marginTop: 3, cursor: canManage ? "pointer" : "default" }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row gap-sm" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
          {showOwner && todo.user && (
            <span className="badge badge-neutral" title="Owner">
              <Icon name="user" size={10} /> {todo.user.name}
            </span>
          )}
          <span
            className="t-primary"
            style={{ textDecoration: todo.is_done ? "line-through" : "none", opacity: todo.is_done ? 0.6 : 1 }}
          >
            {todo.title}
          </span>
          {todo.due_date && (
            <span className={`badge ${overdue ? "badge-danger" : "badge-neutral"}`}>
              <Icon name="calendar" size={10} /> {formatDate(todo.due_date)}
            </span>
          )}
          {todo.remind_at && (
            <span
              className={`badge ${todo.reminded_at ? "badge-neutral" : "badge-warning"}`}
              title={todo.reminded_at ? "Reminder already fired" : "Reminder pending"}
            >
              <Icon name="bell" size={10} /> {formatDateTime(todo.remind_at)}
            </span>
          )}
          {assignedBy && (
            <span className="badge badge-info" title="Assigned to this list by">
              by {assignedBy}
            </span>
          )}
        </div>
        {todo.description && (
          <div className="text-xs t-muted" style={{ marginTop: 3, whiteSpace: "pre-wrap" }}>
            {todo.description}
          </div>
        )}
      </div>
      {canManage && (
        <div className="row gap-sm">
          <button className="icon-btn" title="Edit" onClick={onStartEdit}><Icon name="edit" size={13} /></button>
          <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={onDelete}>
            <Icon name="trash" size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function TodoEditForm({ todo, onCancel, onSave }) {
  const [title, setTitle] = useState(todo.title || "");
  const [due, setDue] = useState(todo.due_date ? String(todo.due_date).slice(0, 10) : "");
  // datetime-local wants "YYYY-MM-DDTHH:mm" — slice the seconds/timezone off
  // the backend's ISO string.
  const [remind, setRemind] = useState(todo.remind_at ? String(todo.remind_at).slice(0, 16) : "");
  const [description, setDescription] = useState(todo.description || "");

  return (
    <div className="col gap-sm" style={{ padding: 12, borderBottom: "1px solid var(--border)", background: "var(--primary-50)" }}>
      <div className="row gap-sm" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
        <div className="field" style={{ flex: 2, minWidth: 200, marginBottom: 0 }}>
          <label className="field-label">Title</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        </div>
        <div className="field" style={{ minWidth: 150, marginBottom: 0 }}>
          <label className="field-label">Due date</label>
          <input type="date" className="input" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 200, marginBottom: 0 }}>
          <label className="field-label">Remind me at</label>
          <input
            type="datetime-local"
            className="input"
            value={remind}
            onChange={(e) => setRemind(e.target.value)}
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label className="field-label">Notes <span className="t-muted text-xs">(optional)</span></label>
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-primary btn-sm"
          disabled={!title.trim()}
          onClick={() => onSave({
            title: title.trim(),
            due_date: due || null,
            remind_at: remind || null,
            description: description || null,
          })}
        >
          <Icon name="check" size={12} /> Save
        </button>
      </div>
    </div>
  );
}
